import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPrint, faSearch, faFileExcel, faSyncAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import logoImage from '../assets/images/logo.jpg';
import QRCode from 'qrcode';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import Fuse from 'fuse.js';

const ArmsLicense = () => {
    const navigate = useNavigate();
    const [gdsrEntries, setGdsrEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);

    // Calculate new entries count
    const newEntriesCount = useMemo(() => {
        return gdsrEntries.filter(entry => entry.isNew).length;
    }, [gdsrEntries]);

    // Fuse.js for fuzzy search
    const fuse = useMemo(() => new Fuse(gdsrEntries, {
        keys: ['token_id', 'local_no', 'name', 'father_name', 'cnic', 'phone_no', 'status'],
        threshold: 0.3,
        includeScore: true
    }), [gdsrEntries]);

    const fetchEntries = useCallback(async () => {
        try {
            const response = await axios.post('https://chaman.gob.pk/eac_backend/arms.php', {
                action: 'get_records',
            });

            if (Array.isArray(response.data)) {
                const now = new Date();
                setLastRefreshTime(now);

                // Parse and attach timestamp, then sort by newest date+time
                const sortedEntries = response.data
                    .map(entry => {
                        // Try to parse the date reliably
                        const timestamp = new Date(entry.date);
                        return {
                            ...entry,
                            timestamp: isNaN(timestamp.getTime()) ? new Date(0) : timestamp
                        };
                    })
                    .sort((a, b) => b.timestamp - a.timestamp); // Newest first

                setGdsrEntries(prevEntries => {
                    const existingIds = new Set(prevEntries.map(item => item.token_id));

                    const newEntries = sortedEntries
                        .filter(entry => !existingIds.has(entry.token_id))
                        .map(entry => ({ ...entry, isNew: true }));

                    const existingEntries = prevEntries
                        .map(entry => ({ ...entry, isNew: false }));

                    return [...newEntries, ...existingEntries];
                });
            } else {
                setGdsrEntries([]);
                console.error('Expected array but got:', response.data);
            }
        } catch (err) {
            setError('Failed to fetch Arms License entries');
            console.error(err);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, []);


    useEffect(() => {
        // Initial load with small delay to show loading state
        const timer = setTimeout(() => {
            fetchEntries();
        }, 5);

        return () => clearTimeout(timer);
    }, [fetchEntries]);

    const refreshData = () => {
        setIsRefreshing(true);
        fetchEntries();
    };

    const filteredEntries = useMemo(() => {
        if (!searchTerm) return gdsrEntries;
        return fuse.search(searchTerm).map(result => result.item);
    }, [gdsrEntries, searchTerm, fuse]);

    const handleDelete = async (token_id) => {
        if (window.confirm(`Are you sure you want to delete the entry with Token ID: ${token_id}?`)) {
            try {
                const response = await axios.post('https://chaman.gob.pk/eac_backend/arms.php', {
                    action: 'delete',
                    token_id,
                });
                if (response.data.status === 'success') {
                    setGdsrEntries(prev => prev.filter(entry => entry.token_id !== token_id));
                } else {
                    throw new Error(response.data.message || 'Failed to delete');
                }
            } catch (error) {
                alert(`Error: ${error.message}`);
            }
        }
    };

    const handleEdit = (entry) => {
        navigate('/dashboard/edit-arms-license', { state: { entry } });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };
    
    const handlePrint = async (entry) => {
        try {
            // Send a request to update print count
            await axios.post('https://chaman.gob.pk/eac_backend/arms.php', {
                action: 'update_print_count',
                token_id: entry.token_id,
                p_count: 'print',
            });
    
            console.log('Print count updated successfully');
    
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const pageHeight = doc.internal.pageSize.getHeight();
            const formattedDate = formatDate(entry.date);

            // Add logo image and text
            doc.setFontSize(10);
            const imageWidth = 0.8 * 28.5;
            const imageHeight = 0.6 * 28.8;
            const xPosition = 14;
            const yPosition = 16;
            doc.addImage(logoImage, 'PNG', xPosition, yPosition, imageWidth, imageHeight);
    
            doc.setFontSize(11);
            doc.text(`No.${entry.local_no} /Arms Branch/Approval`, pageWidth - 72, 22);
            doc.text("DEPUTY COMMISSIONER CHAMAN", pageWidth - 80, 28);
            doc.text(`Dated the ${formatDate(entry.date)}`, pageWidth - 65, 33);
    
            doc.setFontSize(12);
            doc.text('To,', 14, 47);
            doc.text('The Secretary,', 43.5, 55);
            doc.text('Home & Tribal Affairs Department,', 43.5, 60);
            doc.text('Government of Balochistan, Quetta.', 43.5, 65);
    
            doc.setFontSize(13);
            doc.text('Subject:', 14, 80);
            doc.setFont('times', 'bold');
            doc.text('GRANT OF ARMS LICENSE', 43.5, 80);
            doc.setFont('times', 'normal');
            doc.line(43.5, 81, 105, 81);
    
            doc.setFontSize(11);
            doc.text(`Kindly refer to the above noted subject.`, 43.5, 90);
            doc.text(`Application submitted by the following person is sent herewith for approval.`, 43.5, 95);
            doc.text(`${entry.weapon} Arms License.`, 14, 100);
    
            const tableData = [
                ["Sr#", "Name, Father Name, CNIC & Address", "Particulars"],
                ["1", `${entry.name}, S/O ${entry.father_name}, ${entry.cnic}, ${entry.address}`, entry.weapon || ""],
            ];
    
            doc.autoTable({
                head: [tableData[0]],
                body: tableData.slice(1),
                startY: 110,
                theme: 'grid',
                headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255], fontSize: 10, fontStyle: 'bold', halign: 'center' },
                styles: { fontSize: 10, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.1 },
                columnStyles: { 0: { cellWidth: 10, halign: 'center' }, 1: { cellWidth: 141, halign: 'left' }, 2: { cellWidth: 30, halign: 'left' } },
            });
    
            doc.text("DEPUTY COMMISSIONER", 147, pageHeight - 145);
            doc.text("CHAMAN", 163, pageHeight - 140);
    
            const qrData = `Arms License of ${entry.name} has been verified and issued by the Office of the Deputy Commissioner, Chaman.`;
            const qrCanvas = document.createElement('canvas');
            await QRCode.toCanvas(qrCanvas, qrData, { width: 100 });
            const qrDataURL = qrCanvas.toDataURL('image/png');
    
            doc.addImage(qrDataURL, 'PNG', 100, pageHeight - 80, 20, 20);
    
            window.open(doc.output('bloburl'));
        } catch (error) {
            console.error('Error updating print count:', error);
            alert('Failed to update print count. Please try again.');
        }
    };

    const Row = ({ index, style }) => {
        const entry = filteredEntries[index];
        if (!entry) return null;

        return (
            <div style={style} className="table-row">
                <div className="table-cell">{entry.token_id}</div>
                <div className="table-cell">{entry.date}</div>
                <div className="table-cell">{entry.name}</div>
                <div className="table-cell">{entry.father_name}</div>
                <div className="table-cell">{entry.cnic}</div>
                <div className="table-cell actions btn-group">
                    <button onClick={() => handlePrint(entry)} title="Print" className="print-btn">
                        <FontAwesomeIcon icon={faPrint} />
                    </button>
                    <button onClick={() => handleEdit(entry)} title="Edit" className="edit-btn">
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button onClick={() => handleDelete(entry.token_id)} title="Delete" className="delete-btn">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>

            </div>
        );
    };

    if (loading) return (
        <div className="loading-container">
            <div className="modern-loading">
                <div className="loading-wave">
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                </div>
                <p className="loading-text">Loading ...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="error-container">
            <p>Error: {error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
        </div>
    );

    return (
        <div className="certificate-dashboard">
            <div className="dashboard-header">
                <div className="header-left">
                    {/* <h2>Local Certificate Entries</h2> */}
                    <span className="entries-count">Total Records {gdsrEntries.length}</span>
                </div>

                <div className="header-right">
                    <div className="search-refresh-wrapper">
                        <button
                            onClick={refreshData}
                            className="refresh-button"
                            disabled={isRefreshing}
                            aria-label="Refresh data"
                        >
                            <FontAwesomeIcon
                                icon={faSyncAlt}
                                spin={isRefreshing}
                                className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}
                            />
                        </button>

                        <div className="search-bar">
                            <FontAwesomeIcon icon={faSearch} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search locals..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            {searchTerm && (
                                <button className="clear-search" onClick={() => setSearchTerm('')}>
                                    ×
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>




            <div className="table-container">
                <div className="table-header">
                    <div className="header-cell">Token ID</div>
                    <div className="header-cell">Date</div>
                    <div className="header-cell">Name</div>
                    <div className="header-cell">F/H Name</div>
                    <div className="header-cell">CNIC</div>
                    <div className="header-cell">Actions</div>
                </div>

                <div className="table-body">
                    {filteredEntries.length > 0 ? (
                        <AutoSizer>
                            {({ height, width }) => (
                                <List
                                    height={height}
                                    itemCount={filteredEntries.length}
                                    itemSize={45}
                                    width={width}
                                >
                                    {Row}
                                </List>
                            )}
                        </AutoSizer>
                    ) : (
                        <div className="empty-state">
                            <p>No entries found {searchTerm ? `matching "${searchTerm}"` : 'available'}</p>
                        </div>
                    )}
                </div>

                <div className="table-footer">
                    Showing {filteredEntries.length} of {gdsrEntries.length} entries
                </div>
            </div>
        </div>
    );
};

export default ArmsLicense;